import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell, inputCls, Field } from "@/components/AdminTools";
import { Send, Copy, X, RefreshCw, Sparkles, Eye } from "lucide-react";

const EMPTY = { first_name: "", last_name: "", organization: "", email: "" };

export default function AdminDemoAccess() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/demo/invitations")
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.first_name || !form.last_name || !form.organization || !form.email) {
      toast.error("All four fields are required.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/admin/demo/invite", form);
      const url = r.data?.magic_link_url;
      toast.success(`Demo invite sent to ${form.email}`);
      if (url) {
        try {
          await navigator.clipboard.writeText(url);
        } catch {/* clipboard may be blocked */}
      }
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Magic link copied to clipboard.");
    } catch {
      toast.error("Couldn't copy — please copy manually.");
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this demo access link? The user won't be able to sign back in.")) return;
    try {
      await api.post(`/admin/demo/invitations/${id}/revoke`);
      toast.success("Access revoked.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const reseed = async () => {
    try {
      const r = await api.post("/admin/demo/seed");
      toast.success(`Demo network ready · ${r.data?.vendor_count || 0} vendors seeded.`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const [previewing, setPreviewing] = useState(false);
  const openPreview = async () => {
    setPreviewing(true);
    try {
      const r = await api.post("/admin/demo/preview");
      const url = r.data?.magic_link_url;
      if (!url) throw new Error("No preview URL returned");
      // Open in a new tab so the admin keeps their session.
      window.open(url, "_blank", "noopener,noreferrer");
      toast.success("Opening demo portal in a new tab…");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <ToolPageShell
      eyebrow="Admin · Sales tools"
      title="Demo portal access"
      intro="Provision a fully-functional, isolated demo of the clinic portal. Demo users see only the demo vendor network — submissions and invoices stay sandboxed and never trigger real notifications."
      actions={
        <div className="flex items-center gap-2">
          <button
            onClick={openPreview}
            disabled={previewing}
            data-testid="admin-demo-view-portal"
            className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-800 disabled:opacity-60"
          >
            <Eye className="w-3.5 h-3.5" /> {previewing ? "Opening…" : "View demo portal"}
          </button>
          <button
            onClick={reseed}
            data-testid="admin-demo-reseed"
            className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-50"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Re-seed demo network
          </button>
        </div>
      }
    >
      <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 mb-5 flex gap-3">
        <Sparkles className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900 leading-relaxed">
          <div className="font-semibold mb-1">Sandboxed demo experience</div>
          The demo clinic portal mirrors every feature of the production portal — workflow submission, vendor queue, invoices, payment flow — but is fully isolated. No real vendors are notified, no real emails or SMS are sent, and demo data is invisible to production clinics.
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_1.4fr] gap-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 self-start sticky top-20" data-testid="admin-demo-form">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-3">Invite a demo user</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name" required>
              <input
                data-testid="admin-demo-first-name"
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label="Last name" required>
              <input
                data-testid="admin-demo-last-name"
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                className={inputCls}
              />
            </Field>
          </div>
          <Field label="Organization" required>
            <input
              data-testid="admin-demo-org"
              value={form.organization}
              onChange={(e) => setForm({ ...form, organization: e.target.value })}
              className={inputCls}
              placeholder="e.g. Aurora Wellness Clinic"
            />
          </Field>
          <Field label="Email" required hint="We send the magic-link login to this address.">
            <input
              type="email"
              data-testid="admin-demo-email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className={inputCls}
            />
          </Field>
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="admin-demo-submit"
            className="w-full mt-2 inline-flex items-center justify-center gap-2 bg-slate-900 text-white rounded-full px-4 py-2.5 text-sm font-semibold hover:bg-slate-800 disabled:opacity-60"
          >
            <Send className="w-3.5 h-3.5" /> {submitting ? "Creating…" : "Send demo invite"}
          </button>
          <div className="text-[10px] text-slate-400 mt-2 leading-relaxed">
            Magic link valid for 30 days · Email delivery is currently LOG-ONLY (visible in <code>notification_log</code>) · Phase 2 wires real Resend delivery.
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white" data-testid="admin-demo-list">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">Active invitations</div>
            <span className="text-xs text-slate-500">{items.length}</span>
          </div>
          {loading ? (
            <div className="p-6 text-sm text-slate-500">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500">No demo invitations yet.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((it) => {
                const expires = it.expires_at ? new Date(it.expires_at) : null;
                const isExpired = expires && expires < new Date();
                return (
                  <li key={it.id} className="px-5 py-4" data-testid={`admin-demo-row-${it.id}`}>
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate">
                          {it.first_name} {it.last_name} · {it.organization}
                        </div>
                        <div className="text-xs text-slate-500">{it.email}</div>
                        <div className="text-[10px] text-slate-400 mt-1">
                          {it.revoked
                            ? "Revoked"
                            : isExpired
                            ? `Expired ${expires.toLocaleDateString()}`
                            : `Expires ${expires?.toLocaleDateString()}`}
                          {it.last_login_at ? ` · Last seen ${new Date(it.last_login_at).toLocaleDateString()}` : " · Never accessed"}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1.5 items-end">
                        {it.revoked ? (
                          <span className="text-[10px] uppercase font-bold text-rose-700">Revoked</span>
                        ) : isExpired ? (
                          <span className="text-[10px] uppercase font-bold text-amber-700">Expired</span>
                        ) : (
                          <span className="text-[10px] uppercase font-bold text-emerald-700">Active</span>
                        )}
                        {!it.revoked && (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => copy(it.magic_link_url)}
                              data-testid={`admin-demo-copy-${it.id}`}
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 rounded-full px-2 py-1"
                            >
                              <Copy className="w-3 h-3" /> Copy link
                            </button>
                            <a
                              href={it.magic_link_url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-full px-2 py-1"
                            >
                              <Eye className="w-3 h-3" /> Open
                            </a>
                            <button
                              onClick={() => revoke(it.id)}
                              data-testid={`admin-demo-revoke-${it.id}`}
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700 bg-rose-50 hover:bg-rose-100 rounded-full px-2 py-1"
                            >
                              <X className="w-3 h-3" /> Revoke
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </ToolPageShell>
  );
}
